import { on } from '@disy/lib/event-util';
import { px } from '@disy/lib/px';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';

import './autosize-textarea.css';

const COMPONENT_NAME = 'd-autosize-textarea';

export class AutosizeTextarea extends HTMLElement {
  #tearDowns: Teardown[] = [];

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    const textarea = this.#textArea;
    textarea.classList.add('form-control');
    on(textarea, 'input', () => this.#resize());
    this.#resize();

    const observer = new ResizeObserver((entries) => {
      entries.forEach(() => {
        this.#resize(); // Triggers another resize, causing a feedback loop that repeats until no further changes occur
      });
    });
    observer.observe(this);

    this.#tearDowns = [observer];
  }

  disconnectedCallback() {
    this.#tearDowns = tearDown(this.#tearDowns);
  }

  get #textArea(): HTMLTextAreaElement {
    const textarea = this.querySelector<HTMLTextAreaElement>('textarea') ?? document.createElement('textarea');
    if (!textarea.isConnected) {
      this.append(textarea);
    }
    return textarea;
  }

  #resize() {
    const textarea = this.#textArea;
    textarea.style.height = ''; // Unset the height, so that we can measure the scrollHeight afterwards.
    const computedStyle = getComputedStyle(textarea);
    textarea.style.height = `calc(${px(textarea.scrollHeight)} - ${computedStyle.paddingTop} - ${computedStyle.paddingBottom})`;
  }

  set value(text) {
    this.#textArea.value = text;
    this.#resize();
  }

  get value() {
    return this.#textArea.value;
  }
}

customElements.define(COMPONENT_NAME, AutosizeTextarea);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: AutosizeTextarea;
  }
}
